import { useContext, useEffect, useState } from 'react';
import { POSMappingFilterOptions } from '../interfaces/POSMappingFilterOptions';
import { UserContext } from '../../../components/shared/useUser';
import useNswagClient from '../../../hooks/api/useNswagClient';
import { GetPosMappingResponse } from '../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import useLogError from '../../../hooks/useLogError';
import { MappingType } from '../enums/MappingType';

const STORAGE_KEY = 'posMappings.activeTabIndex';

export const usePosMappings = () => { 
  const { t } = useTranslation('posMapping');
  const tabs: { key: MappingType; label: string }[] = [
    { key: MappingType.POS,            label: t('titlePOS') },
  ];

  const [activeTab, setActiveTab] = useState<MappingType>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved !== null) {
      const parsed = Number(saved);
      if (parsed === MappingType.POS) {
        return parsed as MappingType;
      }
    }
    return MappingType.POS; 
  });
  const { selectedStore, isAdmin } = useContext(UserContext);
  const [isLoading, setIsLoading] = useState(false);
  const { getPOSMappings, deletePosMapping } = useNswagClient();
  const [posMappings, setPosMappings] = useState<GetPosMappingResponse[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const { logError } = useLogError();
  const [skuSearch, setSkuSearch] = useState('');
  const [storeSearch, setStoreSearch] = useState('');
  const [itemType, setItemType] = useState<number | undefined>(undefined); 
  const [page, setPage] = useState(0);
  const [selectedEntity, setSelectedEntity] = useState<GetPosMappingResponse | undefined>(undefined);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showResultMessage, setShowResultMessage] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState<string>(() => 'source'); 
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const rowsPerPage = 20;

  const getDefaultSort = (tab: MappingType) => 'source';
  const defaultTake = 20;
  const defaultSkip = 0;
  const defaultDirection = 'desc'; 

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(activeTab));
    setOrderBy(getDefaultSort(activeTab));
  }, [activeTab]);

  // Memoize loadFilterData to prevent unnecessary re-renders
  const loadFilterData = (options: POSMappingFilterOptions, currentItemType?: number) => {
    const {
      sku,
      store,
      sort,
      direction,
      skip,
      take,
    } = options;
    const franchiseId = isAdmin() ? undefined : selectedStore?.franchiseId;
    
    setIsLoading(true);
    getPOSMappings(franchiseId, sku, currentItemType?.toString(), store, sort, direction, skip, take)
      .then((result) => {
        if (result?.data) {
          setTotalCount(result.data?.totalCount ?? 0);
          setPosMappings(result.data.data ?? []);
        }
        setIsLoading(false);
      })
      .catch((error) => {
        logError(error);
        setIsLoading(false);
      });
   
  };

  useEffect(() => {
    setIsInitialLoad(true);
    loadFilterData({
      sku: '',
      store: '',
      mappingType: '',
      sort: getDefaultSort(activeTab),
      direction: defaultDirection,
      skip: defaultSkip,
      take: defaultTake,
    }, undefined);
    setIsInitialLoad(false);
  }, [activeTab]); 

  useEffect(() => {
    if (!isInitialLoad) {
      loadDataTable(orderBy, order, page * rowsPerPage, rowsPerPage);
    }
  }, [page]);
  
  const onChange = (_: unknown, newTab: MappingType) => setActiveTab(newTab);

  const callLoadDataFromAPI = (options: POSMappingFilterOptions, currentItemType?: number) => {
    loadFilterData(options, currentItemType);
  };
  
  const loadDataTable = (sort: string, direction: string, skip: number, take: number) => {
    callLoadDataFromAPI({
      sku: skuSearch,
      store: storeSearch,
      mappingType: '', 
      sort,
      direction,
      skip,
      take,
    }, itemType);
  };
  
  const handleSkuSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setSkuSearch(newSearchValue);
  };
  
  const handleStoreSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchValue = event.target.value;
    setStoreSearch(newSearchValue);
  };

  const handleItemTypeChange = (newItemType: number | undefined) => {
    setItemType(newItemType);
  };
  
  const clearAll = () => {
    setSkuSearch('');
    setStoreSearch('');
    setItemType(undefined);
    setPage(0);
    setOrder('desc');
    setOrderBy(getDefaultSort(activeTab));
    callLoadDataFromAPI({
      sku: '',
      store: '',
      mappingType: '', 
      sort: getDefaultSort(activeTab),
      direction: defaultDirection,
      skip: defaultSkip,
      take: defaultTake,
    }, undefined);
  };
  
  const searchPOSMapping = () => {
    setPage(0);
    callLoadDataFromAPI({
      sku: skuSearch,
      store: storeSearch,
      mappingType: '', 
      sort: orderBy,
      direction: order,
      skip: 0, 
      take: defaultTake,
    }, itemType);
  };

  const maxPages = totalCount
    ? Math.max(1, Math.ceil(totalCount / rowsPerPage))
    : 1;
  const isAsc = order === 'asc';

  const handleSort = (property: string) => {
    const order = isAsc ? 'desc' : 'asc';
    setOrder(order);
    setOrderBy(property);
    loadDataTable(property, order, page * rowsPerPage, rowsPerPage);
  };

  const handleCloseModal = () => {
    setShowEditModal(false);
    setShowDeleteModal(false);
    setSelectedEntity(undefined);
  }; 

  const handleDelete = () => {
    handleCloseModal(); 
    setIsLoading(true);
    const posEntity = selectedEntity as GetPosMappingResponse;
    deletePosMapping({
      source: posEntity?.source ?? '',
      target: posEntity?.target ?? '',
      mappingType: posEntity?.mappingType,
      orderType: posEntity?.orderType,
    })
      .then(() => {
        setShowResultMessage({ open: true, message: t('successDeleteMessage'), severity: 'success' });
        loadDataTable(orderBy, order, page * rowsPerPage, rowsPerPage); 
      })
      .catch(() => { 
        setIsLoading(false);
        setShowResultMessage({ open: true, message: t('errors.deleting'), severity: 'error' });
      })
      .finally(() => setIsLoading(false));
  };

  const edit = (entity: GetPosMappingResponse | undefined) => { 
    setSelectedEntity(entity);
    setShowEditModal(true);
  };

  const openCreateModal = () => {
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setSelectedEntity(undefined);
  };

  const posMappingDelete = (entity: GetPosMappingResponse | undefined) => {
    setSelectedEntity(entity);
    setShowDeleteModal(true);
  };
  return {
    t,
    tabs,      
    activeTab,    
    setActiveTab,
    onChange, 
    totalCount,
    posMappings, 
    handleSort,
    order,
    orderBy,
    skuSearch,
    storeSearch,
    itemType,
    handleStoreSearchChange,
    handleSkuSearchChange,
    handleItemTypeChange,
    searchPOSMapping,
    clearAll,
    posMappingDelete,
    edit,
    openCreateModal,
    closeCreateModal,
    closeEditModal,
    handleDelete,
    handleCloseModal,
    selectedEntity,
    showEditModal,
    showCreateModal,
    showDeleteModal,
    setShowDeleteModal,
    showResultMessage,
    setShowResultMessage,
    isLoading,
    page,
    setPage,
    maxPages,
  };
};